import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import logo from '../assets/img/cm_logo.png';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Badge from '@mui/material/Badge';
import { Typography, alpha } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks';
import { getFrontViewData } from '../slices/frontViewSlice';
import FavoriteDrawer from '../components/frontView/FavoriteDrawer';
import theme from '../utils/theme';
import { Link } from 'react-router-dom';

interface FrontViewLayoutProps {
  children: React.ReactNode;
  window?: () => Window;
}

const drawerWidth = '100%';
const navItems = [
  { title: 'Home', link: '/' },
  { title: 'Jobs', link: 'jobs' },
  { title: 'About', link: 'about-us' }
];

const FrontViewLayout: React.FC<FrontViewLayoutProps> = ({ children, window }) => {
  const dispatch = useAppDispatch();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [favDrawerOpen, setFavDrawerOpen] = React.useState(false);
  const { favIds } = useAppSelector(state => state.favIds);

  const handleDrawerToggle = () => {
    setMobileOpen(prevState => !prevState);
  };

  const handleFavDrawerToggle = () => {
    setFavDrawerOpen(prevState => !prevState);
  };

  const fetchData = React.useCallback(async () => {
    await dispatch(getFrontViewData({ method: 'get', data: null }));
  }, [dispatch]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    if (!localStorage.getItem('favIds')) {
      localStorage.setItem('favIds', JSON.stringify([]));
    }
  }, []);

  const drawer = (
    <Box sx={{ textAlign: 'center' }}>
      <img alt='logo' src={logo} style={{ width: '200px', padding: '5px 0px' }} />
      <Divider />
      <List>
        {navItems.map((item, index) => (
          <Link to={item.link} key={index}>
            <ListItem disablePadding>
              <ListItemButton sx={{ textAlign: 'center', textTransform: 'uppercase' }}>
                <ListItemText primary={item.title} sx={{ color: '#000' }}/>
              </ListItemButton>
            </ListItem>
          </Link>
        ))}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={handleFavDrawerToggle}>
            <Badge badgeContent={favIds.length} color="secondary" sx={{ mx: 'auto' }}>
              <FavoriteBorderIcon />
            </Badge>
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar color='transparent' component="nav" sx={{ boxShadow: 'none', backgroundColor: alpha(theme.palette.primary.main, 0.8) }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            {/* <img alt='logo' src={logo} style={{ height: '40px', marginRight: '10px' }} /> */}
            <Typography className="intro" sx={{ fontSize: { xs: '18px', md: '22px' } }}>CMJ</Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {navItems.map((item, index) => (
              <Link key={index} to={item.link}>
                <Button sx={{ color: '#fff', mr: 2 }} size='large'>
                  {item.title}
                </Button>
              </Link>
            ))}
            <Badge onClick={handleFavDrawerToggle} badgeContent={favIds.length} color="error">
              <FavoriteBorderIcon />
            </Badge>
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          anchor="top"
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
      <FavoriteDrawer
        favDrawerOpen={favDrawerOpen}
        handleFavDrawerToggle={handleFavDrawerToggle}
        favIds={favIds}
      />
      <Box sx={{ width: '100vw', display: 'flex', alignItems: 'flex-start', overflow: 'hidden' }}>
        {children}
      </Box>
    </Box>
  );
}

export default FrontViewLayout;
